import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Inbox, FileText, Settings as SettingsIcon,
  ShieldCheck, LogOut, Menu, X, KeyRound, BarChart3,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/visitors", label: "Visitors", icon: BarChart3 },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
  { to: "/admin/admins", label: "Admins", icon: Users, superOnly: true },
  { to: "/admin/change-password", label: "Change Password", icon: KeyRound },
];

export function AdminLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-[#0A2342] text-white transform transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <ShieldCheck size={22} className="text-[#1E88E5]" />
          <span className="font-heading font-bold tracking-wide text-sm">MERIDIAN ADMIN</span>
        </div>
        <nav className="px-3 py-5 space-y-1">
          {NAV.filter((n) => !n.superOnly || isSuper).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E88E5] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-white/10">
          <div className="px-3 pb-3">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-white/50 truncate">{user?.email}</p>
            <span className="mt-1 inline-block text-[10px] uppercase tracking-wider bg-white/10 rounded px-2 py-0.5">
              {user?.role === "super_admin" ? "Super Admin" : "Admin"}
            </span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-5 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden text-[#0A2342]" onClick={() => setOpen((v) => !v)} data-testid="admin-sidebar-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="hidden lg:block" />
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#1E88E5] hover:underline">
            View Website ↗
          </a>
        </header>
        <main className="p-5 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
